import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { fetchTags, normalizeTagName, TAG_NAME_MAX } from "../lib/media/tags";
import { queryKeys } from "../lib/queryKeys";
import SheetShell from "./SheetShell";

interface Props {
  /** 見出しに出す件数。「3件にタグを付ける」のように使う */
  count: number;
  onPick: (name: string) => void;
  onClose: () => void;
}

/**
 * タグを選ぶ・作るシート。
 *
 * `window.prompt` を置き換える。名前を打ち込むだけだと既にあるタグを選べず、
 * 付けるたびに綴りを思い出す羽目になっていた。
 */
export default function TagSheet({ count, onPick, onClose }: Props) {
  const tags = useQuery({ queryKey: queryKeys.tags, queryFn: fetchTags });
  const [keyword, setKeyword] = useState("");

  const typed = normalizeTagName(keyword);
  const matched = useMemo(() => {
    const all = tags.data ?? [];
    if (typed === null) return all;
    return all.filter((tag) => tag.name.includes(typed));
  }, [tags.data, typed]);

  // 打った名前が既にあるなら「作る」は出さない
  const canCreate = typed !== null && !(tags.data ?? []).some((tag) => tag.name === typed);

  return (
    <SheetShell title={`${String(count)}件にタグを付ける`} onClose={onClose}>
      <div className="p-4 pb-2">
        <input
          autoFocus
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
          }}
          placeholder="タグ名を入力または検索"
          autoComplete="off"
          data-1p-ignore
          maxLength={TAG_NAME_MAX}
          className="w-full rounded-lg border border-[var(--color-line)] px-3 py-2 text-base"
        />
      </div>

      <div className="flex flex-col overflow-y-auto pb-3">
        {canCreate && (
          <button
            type="button"
            onClick={() => {
              onPick(typed);
            }}
            className="flex items-center gap-2 px-4 py-3 text-left text-sm text-[var(--color-teal)]"
          >
            <Plus className="size-4" aria-hidden />「{typed}」を作って付ける
          </button>
        )}

        {matched.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => {
              onPick(tag.name);
            }}
            className="border-t border-[var(--color-line)] px-4 py-3 text-left text-sm"
          >
            {tag.name}
          </button>
        ))}

        {matched.length === 0 && !canCreate && (
          <p className="px-4 py-6 text-center text-sm text-[var(--color-sub)]">
            タグは登録されていません。
          </p>
        )}
      </div>
    </SheetShell>
  );
}
